import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from "@microsoft/signalr";
import { toast } from "sonner";
import { PresenceClient, VybePresenceSelf } from "./types";

/** Resolve the presence server base URL (its own SignalR service, independent of trading). */
function presenceBaseUrl(): string {
  const viteUrl = import.meta.env.VITE_PRESENCE_URL;
  if (viteUrl) return viteUrl;
  if (typeof process !== "undefined" && process.env) {
    return process.env.VITE_PRESENCE_URL || process.env.NEXT_PUBLIC_PRESENCE_URL || "";
  }
  return "";
}

/** How often the terminal pings the hub so its socket isn't reaped as idle/dead. */
const HEARTBEAT_MS = 25_000;

export interface PresenceCallbacks {
  /** The authenticated identity echoed back by the hub on connect. */
  onSelf?: (self: VybePresenceSelf) => void;
  /** Live connection state (drives the header chip blue/grey). */
  onConnectedChange?: (connected: boolean) => void;
  /** An ephemeral one-line admin notification pushed to this exact client. */
  onAdminNotification?: (text: string) => void;
  /**
   * The CRM changed this user's trading accounts (added, removed, or a password op). A pure
   * invalidation signal — no data rides it; the handler re-fetches the synced list over HTTP.
   */
  onAccountsChanged?: () => void;
  /**
   * The server accepted the socket then closed it without ever authenticating us — the session token
   * is expired or revoked. Fired once; the caller should expire the session and prompt a fresh
   * sign-in rather than let presence retry a dead token forever.
   */
  onAuthRejected?: () => void;
}

/**
 * The second, independent SignalR connection: "logged in to Vybe Trader". It carries the session token
 * plus a `?client=` shell hint, heartbeats to stay live, auto-reconnects, and only reports presence — it
 * never touches trading. Kept separate from VybeTraderClient by design.
 */
export class PresenceConnection {
  private connection: HubConnection | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private cbs: PresenceCallbacks = {};
  /** Pending initial-connect retry timer; cleared on success or disconnect. */
  private retry: ReturnType<typeof setTimeout> | null = null;
  /** True between connect() and disconnect() — gates auto-retry so logout stops it. */
  private wanted = false;
  /**
   * Did the hub ever echo VybeOnPresenceSelf on the *current* connection? A close with this still
   * false means the server rejected our token right after the handshake (auth failure) rather than
   * a live session dropping — the two need opposite responses (re-auth vs. reconnect).
   */
  private receivedSelf = false;
  /** Token/client for the active connection, so onclose can self-heal a genuine drop. */
  private activeToken: string | null = null;
  private activeClient: PresenceClient | null = null;

  async connect(
    token: string,
    client: PresenceClient,
    cbs: PresenceCallbacks = {},
  ): Promise<void> {
    const base = presenceBaseUrl();
    if (!base) {
      // No presence URL baked into this build — can't connect. Report offline so the
      // header chip shows the warning state rather than silently pretending all is well.
      this.cbs = cbs;
      this.emitConnected();
      return;
    }
    if (this.connection) return;
    this.cbs = cbs;
    this.wanted = true;
    this.receivedSelf = false;
    this.activeToken = token;
    this.activeClient = client;

    const url = `${base.replace(/\/$/, "")}/PresenceHub?client=${encodeURIComponent(client)}`;
    this.connection = new HubConnectionBuilder()
      .withUrl(url, { accessTokenFactory: () => token })
      .withAutomaticReconnect()
      .build();

    // Identity echo — carries email, displayName, roles, plan. Its arrival is the proof our token
    // authenticated: any later close is a real drop, not an auth rejection.
    this.connection.on("VybeOnPresenceSelf", (self: VybePresenceSelf) => {
      this.receivedSelf = true;
      this.cbs.onSelf?.(self);
    });

    // Ephemeral admin fan-out → Sonner toast on this terminal. Never stored.
    this.connection.on("AdminNotification", (payload: { text?: string }) => {
      const text = typeof payload?.text === "string" ? payload.text : "";
      if (!text) return;
      this.cbs.onAdminNotification?.(text);
    });

    // Account-change invalidation (crm-app → presence → here). Signal only, never data.
    this.connection.on("VybeOnAccountsChanged", () => {
      this.cbs.onAccountsChanged?.();
    });

    this.connection.onreconnected(() => this.emitConnected());
    this.connection.onreconnecting(() => this.emitConnected());
    this.connection.onclose(() => {
      this.stopHeartbeat();
      const authRejected = this.wanted && !this.receivedSelf;
      const droppedAfterAuth = this.wanted && this.receivedSelf;
      this.connection = null;
      this.emitConnected();

      if (authRejected) {
        // Server took the socket then closed it before echoing our identity: the token is dead.
        // withAutomaticReconnect would loop on the SAME dead token forever (the "signed in but
        // presence is down, retrying" state that only a re-login cleared). Stop, and tell the caller
        // to re-authenticate instead.
        this.wanted = false;
        this.cbs.onAuthRejected?.();
        return;
      }

      if (droppedAfterAuth && this.wanted) {
        // A previously-authenticated session whose socket fully closed (auto-reconnect exhausted, or
        // a clean server close). Rebuild it ourselves so presence self-heals without a page reload.
        const token = this.activeToken;
        const client = this.activeClient;
        if (token && client) this.scheduleRetry(token, client);
      }
    });

    try {
      await this.connection.start();
      this.startHeartbeat();
      this.emitConnected();
    } catch {
      // Initial connect failed (server briefly down / transient network). withAutomaticReconnect
      // only covers drops AFTER a successful start, so retry the first connect ourselves with a
      // short backoff — presence should always come up whenever a session exists.
      this.connection = null;
      this.emitConnected();
      if (this.wanted) {
        this.scheduleRetry(token, client);
      }
    }
  }

  /** Re-attempt the initial connect after a delay, unless we've since been asked to disconnect. */
  private scheduleRetry(token: string, client: PresenceClient): void {
    if (this.retry) return;
    this.retry = setTimeout(() => {
      this.retry = null;
      if (!this.wanted || this.connection) return;
      void this.connect(token, client, this.cbs);
    }, 5_000);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = setInterval(() => {
      if (this.connection?.state === HubConnectionState.Connected) {
        void this.connection.send("Heartbeat").catch(() => {});
      }
    }, HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  private emitConnected(): void {
    this.cbs.onConnectedChange?.(this.isConnected);
  }

  async disconnect(): Promise<void> {
    this.wanted = false;
    if (this.retry) {
      clearTimeout(this.retry);
      this.retry = null;
    }
    const conn = this.connection;
    this.connection = null;
    this.stopHeartbeat();
    if (conn) {
      try {
        await conn.stop();
      } catch {
        /* ignore */
      }
    }
    this.emitConnected();
  }

  get isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }
}

export const presenceConnection = new PresenceConnection();

/** Default handler that surfaces an admin notification as a Sonner toast on this terminal. */
export function showAdminNotification(text: string): void {
  toast("Vybe Ops", { description: text });
}
